/*
 * -----------------------------------------------------------------------
 *  <copyright file="audit-log/types/data-view-configuration.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2024 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import Constants from 'utils/constants';
import { FieldChangeGrid } from 'views/pages/admin/audit-log/change-grid';
import AuditEnumValues from 'views/pages/admin/audit-log/definitions/enums';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from 'views/pages/admin/audit-log/service';
import {
    panel_OpraQueries_panel_Panel,
    dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable,
    dataViewDataFilters_OpraQueries_dataViewDataFilters_DataViewFilter,
} from '@queries/generated/central-monitoring.generated';

type RecordProps = {
    CfId: {
        $oid: string;
    };
    DataSource: number;
    DisplayIndex: number;
    IsActive: boolean;
    MainPanelId: {
        $oid: string;
    };
    Name: string;
    DataFilterIds: Array<unknown>;
    DataTableIds: Array<unknown>;
    PanelIds: Array<{ $oid: string } | null>;
    UnfilterablePanelIds: Array<{ $oid: string } | null>;
};

type AdditionalInfo = {
    dataReviewTablesForStudy: dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable[];
    dataViewDataFilters: dataViewDataFilters_OpraQueries_dataViewDataFilters_DataViewFilter[];
    panels: panel_OpraQueries_panel_Panel[];
};

type DataViewConfigurationChangeGridProps = {
    oldRecord: Partial<RecordProps>;
    newRecord: Partial<RecordProps>;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
    additionalInfo: AdditionalInfo;
};

const propArray = ['CfId', 'MainPanelId', 'DataSource', 'DisplayIndex', 'Name', 'UnfilterablePanelIds'];

const DataViewConfigurationChangeGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    additionalInfo,
}: DataViewConfigurationChangeGridProps) => {
    const enums = {
        DataSource: AuditEnumValues.DataSourceTableType,
    };

    const enumMarkerByField = {
        FieldType: '_FIELD_TYPE',
    };

    const getMainPanelName = (record: Partial<RecordProps>, addInfo: AdditionalInfo) => {
        if (!record || !record.MainPanelId?.$oid || record.MainPanelId.$oid === Constants.ObjectId.Empty) {
            return null;
        }

        return addInfo.panels.find((el: panel_OpraQueries_panel_Panel) => el.id === record.MainPanelId?.$oid)?.graph
            ?.plotTitle;
    };

    const refactorRecord = (
        record: Partial<RecordProps>,
        addInfo: AdditionalInfo,
        hasUnfilterableChanges: boolean,
        item?: Record<string, unknown>,
    ) => {
        let nonFilterablePanels = null;
        // This feels a bit wrong, but when want to show the whole list and not just the entries that changed
        if (hasUnfilterableChanges && item && item.unfilterablePanelIds) {
            nonFilterablePanels = (item.unfilterablePanelIds as string[])
                .map((el) => {
                    const panel = addInfo.panels.find((p) => p.id === el);
                    return panel?.graph?.plotTitle;
                })
                .sort()
                .join(', ');
        }

        return {
            ...record,
            DisplayOrder: record.DisplayIndex,
            ConfigId: record.CfId?.$oid,
            dataViewName: record.Name,
            MainPanelName: getMainPanelName(record, addInfo),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
            dataSource: Constants.DataSourceLOV.find((d) => d.value === record.DataSource)?.key,
            nonFilterablePanels,
            ...removePropertiesFromObject(record, propArray),
        } as Record<string, unknown>;
    };

    const hasUnfilterableChanges =
        !!oldRecord.UnfilterablePanelIds?.filter((el) => el !== null).length ||
        !!newRecord.UnfilterablePanelIds?.filter((el) => el !== null).length;

    const refactoredOldRecord = refactorRecord(oldRecord, additionalInfo, hasUnfilterableChanges, oldItem);
    const refactoredNewRecord = refactorRecord(newRecord, additionalInfo, hasUnfilterableChanges, newItem);

    const arrayOfObjectsV2 = {
        PanelIds: {
            valueResolver: (recordData: unknown, index: number) => {
                if (!recordData) {
                    return null;
                }
                const data = recordData as { $oid: string };
                const panel = additionalInfo.panels.find((el) => el.id === data.$oid);
                if (!panel) {
                    return null;
                }

                return `${index + 1}: ${panel.recordId} ${panel.graph?.plotTitle}`;
            },
            compareField: '$oid',
        },
        DataTableIds: {
            valueResolver: (recordData: unknown, index: number) => {
                if (!recordData) {
                    return null;
                }

                const data = recordData as { $oid: string };
                const table = additionalInfo.dataReviewTablesForStudy.find((el) => el.id === data.$oid);
                if (!table) {
                    return null;
                }

                return `${index + 1}: ${table.recordId} ${table.name}`;
            },
            compareField: '$oid',
        },
        DataFilterIds: {
            valueResolver: (recordData: unknown, index: number) => {
                if (!recordData) {
                    return null;
                }

                const data = recordData as { $oid: string };
                const filter = additionalInfo.dataViewDataFilters.find((el) => el.id === data.$oid);
                if (!filter) {
                    return null;
                }

                return `${index + 1}: Label: ${filter.label} Field: ${filter.fieldKey}`;
            },
            compareField: '$oid',
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) =>
        getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            enums,
            enumMarkerByField,
            arrayOfObjectsV2,
        }),
    );

    return <FieldChangeGrid items={items} />;
};

export { DataViewConfigurationChangeGrid };
